/* eslint-disable max-lines */
import { CircularProgress, makeStyles } from '@material-ui/core';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useContext } from 'react';
import MessagesContext from '../../../../contexts/messagesContext';
import { CancelButton, ClinicInput, SubmitButton } from '../../../UI';
import { CloseIcon, TrashIcon } from '../../../UI/Images/Icons';
import ClinicAutocomplete from '../../../UI/inputs/ClinicAutocomplete';
import RadioButton from '../../../UI/inputs/RadioButton';
import PermissionsList from './PermissionsList';
import { useDispatch, useSelector } from 'react-redux';
// import { findUser } from '../../../redux/User/UserAction';
import { PropTypes } from 'prop-types';
import UsernameField from './UsernameField';
import {
    createUser,
    editUserAccess,
    getUser,
    resetUserFound
} from '../../../../redux/User/UserAction';
import { getNamespace } from '../../../../utils/storage';
import * as type from '../../../../redux/User/UserType';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { createLoadingSelector } from '../../../../redux/Loading/LoadingSelector';

const styles = makeStyles({
    container: {
        minWidth: '500px',
        minHeight: '500px',
        padding: '10px 24px',
        position: 'relative'
    },
    title: {
        marginBottom: '20px'
    },
    delegateContainer: {
        margin: '5px 22px 0 0'
    },
    label: {
        fontSize: '13px',
        fontWeight: 'bold',
        color: '#989ea3'
    },
    inputWrapper: {
        width: '210px',
        minHeight: '40px'
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
        // marginTop: '10px'
    },
    radioWrapper: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px'
        // marginTop: '10px'
    },
    tag: {
        borderRadius: '4px',
        backgroundColor: '#e8ecf2',
        color: '#232733',
        fontFamily: 'inherit',
        fontSize: '13px',
        '& svg': {
            margin: '0px 3px !important'
            // backgroundColor: 'transparent'
        }
    },
    buttonsWrapper: {
        marginTop: '20px',
        display: 'flex',
        justifyContent: 'flex-end'
        // marginInlineStart: 'auto'
    },
    deleteUser: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        '& span': {
            marginInlineStart: '5px',
            color: '#ee384e'
        }
    },
    userInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    name: {
        display: 'block',
        fontSize: '15px',
        fontWeight: 'bold'
    },
    username: {
        color: '#989ea3',
        fontSize: '13px',
        fontWeight: '500',
        marginTop: '10px'
    },
    selectClinic: {
        width: '450px'
    }
});
const AddOrEditUser = ({
    userId,
    isYou,
    isOwner,
    isEditing = false,
    setDialog,
    setAlertDialog
}) => {
    const classes = styles();
    const dispatch = useDispatch();
    const { messages } = useContext(MessagesContext);
    const namespace = getNamespace();
    const user = useSelector((state) => state?.user?.user) || {};
    // const userFound = useSelector((state) => state?.user?.userFound);
    const { namespace_access } = user;
    const clinics = namespace_access?.clinics.map((item) => item.id);
    const polyclinic = useSelector((state) =>
        state?.user?.polyclinics?.find((item) => item.id === namespace)
    );
    const loadingSelector = createLoadingSelector(type.GET_USER.REQUEST);
    const isLoading = useSelector(loadingSelector);
    const [options, setOptions] = useState([]);
    const [userSelected, setUserSelected] = useState(false);
    const history = useHistory();

    const userInfo = {
        username: '',
        first_name: '',
        last_name: '',
        password: ''
    };
    const clinicOrPolyClinic = [
        { label: messages.clinic, value: false },
        { label: messages.polyclinic, value: true }
    ];
    const validation = yup.object({
        username: yup.string().required(messages.validations.required),
        first_name: yup.string().required(messages.validations.required),
        password: yup.string().required(messages.validations.required),
        clinics: yup.array().when('is_assignee', {
            is: (val) => !val,
            then: (schema) => schema.required(messages.validations.required)
        })
    });
    const editValidation = yup.object({
        clinics: yup.array().when('is_assignee', {
            is: (val) => !val,
            then: (schema) => schema.required(messages.validations.required)
        })
    });

    useEffect(() => {
        if (userId) {
            dispatch(getUser(userId));
        }
        return () => {
            dispatch({
                type: type.GET_USER.SUCCESS,
                payload: null
            });
        };
    }, [userId]);

    useEffect(() => {
        return () => {
            if (!isEditing) {
                dispatch(resetUserFound());
            }
        };
    }, [isEditing]);

    useEffect(() => {
        if (polyclinic?.clinics?.length) {
            setOptions(polyclinic?.clinics?.map((item) => ({ id: item.id, name: item.name })));
        }
    }, [polyclinic]);

    const handleCancel = (resetForm) => {
        resetForm();
        setDialog(false);
    };

    const handleSubmit = async (value, setSubmitting) => {
        if (user?.username) {
            const response = await dispatch(editUserAccess(user?.id, value, isYou));
            const status = response?.status;
            if (status === 200 || status === 201) {
                setSubmitting(false);
                setDialog(false);
                if (isYou) {
                    history.replace('/patients');
                }
            }
        } else {
            const response = await dispatch(createUser(value));
            const status = response?.status;
            if (status === 200 || status === 201) {
                setSubmitting(false);
                setDialog(false);
            }
        }
    };

    if (isLoading) {
        return (
            <div className={classes.container}>
                <div className="loadingContainer">
                    <CircularProgress />
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <Formik
                enableReinitialize
                validationSchema={!user?.username ? validation : editValidation}
                initialValues={{
                    ...(!user?.username && { ...userInfo }),
                    namespace: namespace,
                    is_assignee: namespace_access?.is_assignee || false,
                    clinics: clinics || [],
                    permissions: namespace_access?.permissions ?? {
                        write_demographic: false,
                        read_clinical: false,
                        write_clinical: false,
                        aggregate_data: false,
                        manage_form: false,
                        manage_user: false,
                        manage_clinic: false,
                        delegate_write_demographic: false,
                        delegate_read_clinical: false,
                        delegate_write_clinical: false,
                        delegate_aggregate_data: false,
                        delegate_manage_form: false,
                        delegate_manage_user: false,
                        delegate_manage_clinic: false
                    }
                }}
                onSubmit={(value, { setSubmitting }) => handleSubmit(value, setSubmitting)}
            >
                {({ values, setFieldValue, isSubmitting, resetForm, ...props }) => (
                    <Form>
                        <div>
                            {isEditing ? (
                                <div className={classes.userInfo}>
                                    <div>
                                        <span className={classes.name}>
                                            {user.first_name} {user.last_name}
                                        </span>
                                        <span className={classes.username}>{user.username}</span>
                                    </div>
                                    {!isOwner && (
                                        <div
                                            className={classes.deleteUser}
                                            onClick={() => setAlertDialog(true)}
                                        >
                                            <TrashIcon width="20" height="20" />
                                            <span>{messages.deleteUser}</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <h3 className={classes.title}>
                                        {userSelected ? messages.editUser : messages.newUser}
                                    </h3>
                                    <div className={classes.inputWrapper}>
                                        <UsernameField
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            setUserSelected={setUserSelected}
                                            {...props}
                                        />
                                    </div>
                                    {!userSelected && !user?.username && (
                                        <>
                                            <div className={classes.row}>
                                                <div className={classes.inputWrapper}>
                                                    <ClinicInput
                                                        label={messages.firstName}
                                                        name="first_name"
                                                        type="text"
                                                        width="100%"
                                                    />
                                                </div>
                                                <div className={classes.inputWrapper}>
                                                    <ClinicInput
                                                        label={messages.lastName}
                                                        name="last_name"
                                                        type="text"
                                                        width="100%"
                                                    />
                                                </div>
                                            </div>

                                            <div className={classes.inputWrapper}>
                                                <ClinicInput
                                                    label={messages.login.password}
                                                    name="password"
                                                    type="text"
                                                    width="100%"
                                                />
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                            <div className={classes.radioWrapper}>
                                <span>
                                    {messages.addUser} {messages.alerts.at}
                                </span>
                                {clinicOrPolyClinic.map((item, index) => (
                                    <RadioButton
                                        key={index}
                                        label={item.label}
                                        value={item.value}
                                        groupValue={values.is_assignee}
                                        handleChange={({ target: { value } }) => {
                                            setFieldValue(
                                                'is_assignee',
                                                value === 'true' ? true : false
                                            );
                                            setFieldValue('clinics', []);
                                        }}
                                    />
                                ))}
                            </div>

                            <div className={classes.selectClinic}>
                                <ClinicAutocomplete
                                    width="100%"
                                    disabled={values.is_assignee}
                                    options={options}
                                    label={messages.clinic}
                                    name="clinics"
                                    value={values.clinics}
                                    customIcon={null}
                                    setValue={(value) => {
                                        setFieldValue('clinics', value);
                                    }}
                                    customClasses={classes}
                                    disableEndAdornment
                                    noOptionsText={messages.noClinicFound}
                                    chipProps={{
                                        deleteIcon: (
                                            <CloseIcon width="19px" height="19px" color="#545e66" />
                                        )
                                    }}
                                />
                            </div>

                            <div>
                                <label className={classes.label}>{messages.permission}</label>
                                <PermissionsList value={values} setFieldValue={setFieldValue} />
                            </div>

                            {values.permissions.manage_user && (
                                <div className={classes.delegateContainer}>
                                    <label className={classes.label}>
                                        {messages.delegatePerm}:
                                    </label>
                                    <PermissionsList
                                        isDelegate
                                        value={values}
                                        setFieldValue={setFieldValue}
                                        disabled
                                    />
                                </div>
                            )}

                            <div className={classes.buttonsWrapper}>
                                <CancelButton
                                    cancelText={messages.cancel}
                                    onClick={() => handleCancel(resetForm)}
                                    customColor="#989ea3"
                                    disable={isSubmitting}
                                    width="96"
                                />
                                <SubmitButton
                                    isSubmitting={isSubmitting}
                                    submitText={
                                        isEditing || userSelected
                                            ? messages.saveChanges
                                            : messages.userRegistration
                                    }
                                    width="128"
                                />
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};
AddOrEditUser.propTypes = {
    userId: PropTypes.number,
    isYou: PropTypes.number,
    isOwner: PropTypes.number,
    isEditing: PropTypes.bool,
    setDialog: PropTypes.function,
    setAlertDialog: PropTypes.function
};
export default AddOrEditUser;
